import { useModalStore } from '@/store/apps/modalStore';

import generateUniqueId from '@/utils/generateUniqueId';

import { AlertModal, ConfirmModal } from '@/components/shared/ui/modals';
import type { ModalType } from '@/components/shared/ui/modals/types';
import { UploadFile } from '@/components/shared/ui/upload/types';

export default function useModal() {
  const { openModal } = useModalStore();

  // Alert 모달
  const showAlert = ({
    size,
    color,
    title,
    content,
    submitButtonText,
    closeOnClickOutside,
    onSubmit,
  }: {
    size?: 'xs' | 'sm';
    color?: 'white';
    title?: React.ReactNode;
    content?: React.ReactNode;
    submitButtonText?: string;
    closeOnClickOutside?: boolean;
    onSubmit?: () => void;
  }) => {
    openModal({
      id: generateUniqueId('modal'),
      component: AlertModal,
      size,
      color,
      title,
      content,
      submitButtonText,
      closeOnClickOutside,
      onSubmit,
    });
  };

  // Confirm 모달
  const showConfirm = ({
    size,
    color,
    title,
    content,
    closeButtonText,
    submitButtonText,
    closeOnClickOutside,
    onSubmit,
    onClose,
  }: {
    size?: 'xs' | 'sm';
    color?: 'white';
    title?: React.ReactNode;
    content?: React.ReactNode;
    closeButtonText?: string;
    submitButtonText?: string;
    closeOnClickOutside?: boolean;
    onSubmit?: () => void;
    onClose?: () => void;
  }) => {
    openModal({
      id: generateUniqueId('modal'),
      component: ConfirmModal,
      size,
      color,
      title,
      content,
      closeButtonText,
      submitButtonText,
      closeOnClickOutside,
      onSubmit,
      onClose,
    });
  };

  // 커스텀 모달
  const showCustomModal = ({
    component,
    size,
    color,
    title,
    content,
    closeOnClickOutside,
    onSubmit,
    onClose,
    ...props
  }: {
    component: React.ElementType;
    size?: ModalType['size'];
    color?: ModalType['color'];
    title: React.ReactNode;
    content?: React.ReactNode;
    closeOnClickOutside?: boolean;
    onSubmit?: () => void;
    onClose?: () => void;
    [key: string]: any;
  }) => {
    openModal({
      id: generateUniqueId('modal'),
      component,
      size,
      color,
      title,
      content,
      closeOnClickOutside,
      onSubmit,
      onClose,
      ...props,
    });
  };

  // Crop 모달
  const showCropModal = ({
    component,
    size,
    title,
    submitButtonText,
    closeButtonText,
    closeOnClickOutside,
    onSubmit,
    onClose,
    ...props
  }: {
    component: React.ElementType;
    size?: ModalType['size'];
    title: string;
    submitButtonText?: string;
    closeButtonText?: string;
    closeOnClickOutside?: boolean;
    onSubmit?: (croppedFile: File) => void;
    onClose?: () => void;
    [key: string]: any;
  }) => {
    openModal({
      id: generateUniqueId('modal'),
      component,
      size,
      title,
      submitButtonText,
      closeButtonText,
      closeOnClickOutside,
      onSubmit,
      onClose,
      ...props,
    });
  };

  // ImagePreview 모달 - 형태가 달라 별도로 분리
  const showImageModal = ({
    component,
    size,
    closeOnClickOutside,
    onClose,
    ...props
  }: {
    component: React.ElementType;
    size?: ModalType['size'];
    closeOnClickOutside?: boolean;
    onSubmit?: (croppedFile: UploadFile) => void;
    onClose?: () => void;
    [key: string]: any;
  }) => {
    openModal({
      id: generateUniqueId('modal'),
      component,
      size,
      closeOnClickOutside,
      onClose,
      ...props,
    });
  };

  return {
    showAlert,
    showConfirm,
    showCustomModal,
    showCropModal,
    showImageModal,
  };
}
